import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Upload, Trash2, FileText, File, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";

export function DocumentsTab({ tripId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const fileInputRef = useRef(null);

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/documents`);
      setDocs(data);
    } catch {
      // silent
    } finally {
      setLoadingDocs(false);
    }
  }, [tripId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let successCount = 0;
    for (const file of Array.from(files)) {
      try {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/trips/${tripId}/documents`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successCount++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Erreur avec ${file.name}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? "s" : ""} ajouté${successCount > 1 ? "s" : ""}`);
      await loadDocs();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Supprimer "${filename}" ?`)) return;
    try {
      await axios.delete(`${API}/trips/${tripId}/documents/${docId}`);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Document supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDownload = (docId) => {
    window.open(`${API}/trips/${tripId}/documents/${docId}/download`, "_blank");
  };

  const getFileIcon = (fileType) => {
    if (fileType === "pdf") return <FileText size={18} strokeWidth={1.5} className="text-red-500" />;
    if (fileType === "image") return <File size={18} strokeWidth={1.5} className="text-blue-500" />;
    return <File size={18} strokeWidth={1.5} className="text-[#8B5E34]" />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="space-y-5">
      <div className="bg-[#FDF8F3] border border-[#DCD4C6] p-4 rounded-sm">
        <p className="text-sm text-[#8B5E34] font-medium mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>
          Coffre-fort voyage
        </p>
        <p className="text-xs text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          Stockez vos billets d'avion, de train, confirmations d'hôtel, vouchers... Votre conseiller voyage peut lire vos PDF et répondre à toutes vos questions sur ces documents.
        </p>
      </div>

      <div
        className="border-2 border-dashed border-[#DCD4C6] p-8 text-center transition-colors hover:border-[#C17F59] cursor-pointer"
        style={{ borderRadius: "2px" }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
        data-testid="documents-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.docx,.doc,.xlsx,.txt,.csv"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
          data-testid="documents-file-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-[#C17F59] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>Envoi en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border border-[#DCD4C6] rounded-full flex items-center justify-center">
              <Upload size={20} strokeWidth={1.5} className="text-[#C17F59]" />
            </div>
            <div>
              <p className="text-sm text-[#2B2623]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                Cliquez ou glissez vos fichiers ici
              </p>
              <p className="text-xs text-[#9A8F86] italic mt-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                PDF, images, DOCX, Excel — max 15 Mo par fichier
              </p>
            </div>
          </div>
        )}
      </div>

      {loadingDocs ? (
        <div className="text-center py-6">
          <div className="w-6 h-6 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-6 text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          Aucun document ajouté pour ce voyage
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-[#8B5E34]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            {docs.length} document{docs.length > 1 ? "s" : ""}
          </p>
          {docs.map((doc) => (
            <div key={doc.id} className="card-surface flex items-center gap-3 animate-fade-in" data-testid={`doc-item-${doc.id}`}>
              {getFileIcon(doc.file_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2B2623] truncate" style={{ fontFamily: "'Lora', Georgia, serif" }}>{doc.original_filename}</p>
                <div className="flex items-center gap-3 text-xs text-[#9A8F86] mt-0.5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                  <span>{formatSize(doc.file_size)}</span>
                  {doc.has_extracted_text && (
                    <span className="text-emerald-600 italic">Lu par votre conseiller</span>
                  )}
                  {!doc.has_extracted_text && doc.file_type !== "image" && (
                    <span className="text-[#9A8F86] italic">Stocké</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleDownload(doc.id, doc.original_filename)}
                  className="p-1.5 text-[#9A8F86] hover:text-[#C17F59] transition-colors"
                  title="Télécharger"
                  data-testid={`doc-download-${doc.id}`}
                >
                  <Download size={14} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => handleDelete(doc.id, doc.original_filename)}
                  className="p-1.5 text-[#9A8F86] hover:text-red-500 transition-colors"
                  title="Supprimer"
                  data-testid={`doc-delete-${doc.id}`}
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
